import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

/**
 * SessionTimeoutModal
 *
 * Premium glassmorphism warning modal — Hairchitech design system.
 * Fully i18n-aware (EN / FR via common.json "session" namespace).
 *
 * Props — UNCHANGED:
 *   secondsLeft  {number}    Remaining seconds before forced logout.
 *   onExtend     {function}  User clicks "Stay logged in".
 *   onLogout     {function}  User clicks "Log out now".
 */
const SessionTimeoutModal = ({ secondsLeft, onExtend, onLogout }) => {
  const { t } = useTranslation();          // resolves common.json (defaultNS)
  const extendBtnRef = useRef(null);

  useEffect(() => {
    extendBtnRef.current?.focus();
  }, []);

  // Countdown display — unchanged logic
  const minutes     = Math.floor(secondsLeft / 60);
  const seconds     = secondsLeft % 60;
  const displayTime = minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}`
    : `${seconds}s`;

  // Warm amber shift only in the last 5 seconds — less aggressive than hard red
  const isLastFive = secondsLeft <= 5;

  return (
    // ── Overlay ────────────────────────────────────────────────────────────
    <motion.div
      role="alertdialog"
      aria-modal="true"
      aria-label={t('session.expiringTitle')}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.18, ease: 'easeOut' }}
      style={styles.overlay}
    >
      {/* ── Card ─────────────────────────────────────────────────────────── */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
        style={styles.card}
      >

        {/* Thin clock icon — no background chip, just the stroke glyph */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="28" height="28"
          viewBox="0 0 24 24"
          fill="none"
          stroke="rgba(201,160,56,0.75)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ marginBottom: '2px', flexShrink: 0 }}
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>

        {/* Title */}
        <h2 style={styles.title}>{t('session.expiringTitle')}</h2>

        {/* Body */}
        <p style={styles.body}>
          {t('session.expiringMessage')}
          <br />
          <span style={styles.subMessage}>{t('session.expiringSubMessage')}</span>
        </p>

        {/* Countdown */}
        <div
          style={{
            ...styles.countdown,
            color: isLastFive ? '#e6b84a' : '#c9a038',
            textShadow: isLastFive
              ? '0 0 28px rgba(230,184,74,0.55)'
              : '0 0 22px rgba(201,160,56,0.4)',
          }}
        >
          {displayTime}
        </div>

        {/* Hairline divider */}
        <div style={styles.divider} />

        {/* Actions */}
        <div style={styles.actions}>
          <PrimaryButton ref={extendBtnRef} onClick={onExtend}>
            {t('session.stayLoggedIn')}
          </PrimaryButton>
          <SecondaryButton onClick={onLogout}>
            {t('session.logoutNow')}
          </SecondaryButton>
        </div>

      </motion.div>
    </motion.div>
  );
};

// ── Hover-aware sub-components ────────────────────────────────────────────────

const PrimaryButton = React.forwardRef(({ onClick, children }, ref) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      ref={ref}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.primaryBtn,
        background: hovered
          ? 'linear-gradient(135deg, #d4aa42 0%, #b8902f 100%)'
          : 'linear-gradient(135deg, #c9a038 0%, #a8822f 100%)',
        boxShadow: hovered
          ? '0 0 28px rgba(201,160,56,0.4), 0 4px 16px rgba(0,0,0,0.3)'
          : '0 0 20px rgba(201,160,56,0.25), 0 2px 8px rgba(0,0,0,0.2)',
        transform: hovered ? 'translateY(-1px)' : 'translateY(0)',
      }}
    >
      {children}
    </button>
  );
});
PrimaryButton.displayName = 'PrimaryButton';

const SecondaryButton = ({ onClick, children }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        ...styles.secondaryBtn,
        backgroundColor: hovered ? 'rgba(255,255,255,0.08)' : 'transparent',
      }}
    >
      {children}
    </button>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.60)',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 99999,
    padding: '16px',
  },

  card: {
    background: 'rgba(20,20,20,0.55)',
    backdropFilter: 'blur(16px)',
    WebkitBackdropFilter: 'blur(16px)',
    border: '1px solid rgba(255,255,255,0.06)',
    borderRadius: '20px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.05)',
    padding: '40px 32px 32px',
    maxWidth: '400px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },

  title: {
    margin: 0,
    fontSize: '19px',
    fontWeight: 600,
    color: '#ffffff',
    letterSpacing: '-0.3px',
    textAlign: 'center',
  },

  body: {
    margin: 0,
    fontSize: '13.5px',
    color: 'rgba(255,255,255,0.45)',
    lineHeight: 1.7,
    textAlign: 'center',
  },

  subMessage: {
    color: 'rgba(255,255,255,0.35)',
    fontSize: '13px',
  },

  countdown: {
    fontSize: '54px',
    fontWeight: 800,
    letterSpacing: '-2px',
    lineHeight: 1,
    marginTop: '2px',
    marginBottom: '2px',
    fontVariantNumeric: 'tabular-nums',
    transition: 'color 0.4s ease, text-shadow 0.4s ease',
  },

  divider: {
    width: '100%',
    height: '1px',
    background: 'rgba(255,255,255,0.06)',
    margin: '4px 0',
  },

  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    width: '100%',
  },

  primaryBtn: {
    width: '100%',
    padding: '13px 0',
    border: 'none',
    borderRadius: '12px',
    fontSize: '14.5px',
    fontWeight: 600,
    color: '#1a1208',
    cursor: 'pointer',
    letterSpacing: '0.1px',
    transition: 'background 0.2s ease, box-shadow 0.2s ease, transform 0.15s ease',
    outline: 'none',
  },

  secondaryBtn: {
    width: '100%',
    padding: '12px 0',
    border: '1px solid rgba(255,255,255,0.20)',
    borderRadius: '12px',
    fontSize: '14px',
    fontWeight: 400,
    color: 'rgba(255,255,255,0.65)',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    outline: 'none',
  },
};

export default SessionTimeoutModal;
